import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { InviteCarrierModal } from './InviteCarrierModal';
import { Truck, Users, Clock, CheckCircle, MapPin, Calendar, Weight, DollarSign, RefreshCw, Plus, Share2, Copy, ChevronRight, ChevronLeft, Package, Menu, X, Route, UserPlus } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { LoadRequestChat } from "./LoadRequestChat";
import { CarrierManagement } from "./CarrierManagement";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { AppHeader } from "./AppHeader";
import { SettingsDropdown } from "./SettingsDropdown";
import { useToast } from "@/hooks/use-toast";
// Notification will be automatically queued by database trigger
import { useTranslation } from "react-i18next";
import { useRouteDisplay } from '@/utils/routeUtils';
import { generateInviteLink, formatUrlForDisplay } from '@/utils/linkUtils';
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import OrganizationSelector from "./OrganizationSelector";
import OrganizationDashboard from "./OrganizationDashboard";
import { formatAppDate } from "@/lib/utils";
import { useNavigate } from "react-router-dom";

interface LoadOrder {
  id: string;
  title: string;
  description?: string;
  cargo_type: string;
  cargo_weight?: number;
  cargo_value?: number;
  vehicles_needed?: number;
  vehicles_assigned?: number;
  vehicles_active?: number;
  status: string;
  created_at: string;
  created_by: string;
  load_routes: LoadRoute[];
}

interface LoadRoute {
  id: string;
  title: string;
  pickup_city: string;
  delivery_city: string;
  status: string;
  pickup_date: string;
  delivery_date: string;
  vehicle_type?: string;
  vehicles_needed?: number;
  vehicles_assigned?: number;
  vehicles_active?: number;
}

interface LoadAssignment {
  id: string;
  load_route_id: string;
  carrier_org_id?: string;
  customer_org_id?: string;
  status: string;
  price_per_truck?: number;
  total?: number;
  currency?: string;
  vehicles_accepted?: number;
  vehicles_assigned?: number;
  vehicles_canceled?: number;
  vehicles_active?: number;
  vehicles_completed?: number;
  carrier_notes?: string;
  assigned_at: string;
  accepted_at?: string;
  carrier?: any;
  load_routes: {
    id: string;
    title: string;
    load_order_id: string;
  };
}

interface DashboardProps {
  companyName: string;
}

export const Dashboard = ({ companyName }: DashboardProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { formatRoute } = useRouteDisplay();
  const [activeView, setActiveView] = useState<"dashboard" | "new-load" | "carriers">("dashboard");
  const [loadOrders, setLoadOrders] = useState<LoadOrder[]>([]);
  const [loadAssignments, setLoadAssignments] = useState<LoadAssignment[]>([]);
  const [activeAssignments, setActiveAssignments] = useState<LoadAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [orderFilter, setOrderFilter] = useState<"published" | "assigned" | "completed" | "canceled" | null>(null);
  const [selectedRoute, setSelectedRoute] = useState<LoadRoute | null>(null);
  const [selectedCarrier, setSelectedCarrier] = useState<LoadAssignment | null>(null);
  const [selectedLoadOrderId, setSelectedLoadOrderId] = useState<string | null>(null);
  const [precomputedCarrierRoutes, setPrecomputedCarrierRoutes] = useState<any[]>([]);
  const [realtimeChannel, setRealtimeChannel] = useState<any>(null);
  const [isRouteDialogOpen, setIsRouteDialogOpen] = useState(false);
  const [isCarrierDialogOpen, setIsCarrierDialogOpen] = useState(false);
  const [animatingOrders, setAnimatingOrders] = useState<Set<string>>(new Set());
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [defaultInviteLink, setDefaultInviteLink] = useState<string | null>(null);
  const [hasExistingInvitations, setHasExistingInvitations] = useState(false);
  const [hasActiveConnections, setHasActiveConnections] = useState(false);
  const [loadingInviteLink, setLoadingInviteLink] = useState(false);
  const [hasEverCreatedOrders, setHasEverCreatedOrders] = useState(true); // Default to true to avoid flash
  const { toast } = useToast();
  const { isRTL, language } = useLanguage();
  const { user } = useAuth();
  const { currentOrganization, hasOrganization, loading: orgLoading, refreshOrganizations } = useOrganization();

  const updateLoadOrderAggregates = (assignments: LoadAssignment[]) => {
    console.log('🔄 REALTIME: Starting updateLoadOrderAggregates with', assignments.length, 'assignments');
    
    setLoadOrders(prev => {
      console.log('🔄 REALTIME: Current loadOrders count:', prev.length);
      
      const updated = prev.map(order => {
        // Get assignments for this order through routes
        const orderRouteIds = order.load_routes?.map(route => route.id) || [];
        const orderAssignments = assignments.filter(a => orderRouteIds.includes(a.load_route_id));
        // Include all assignments that have vehicles assigned (not just those with vehicles_assigned > 0)
        const activeAssignments = orderAssignments.filter(a => a.status === 'accepted' || a.status === 'pending');
        // Calculate total assigned trucks from all assignments for this order
        const totalAssigned = activeAssignments.reduce((sum, a) => sum + (a.vehicles_assigned || 0), 0);
        
        const oldAssigned = order.vehicles_assigned || 0;
        
        // Update individual routes as well
        const updatedRoutes = order.load_routes?.map(route => {
          const routeAssignments = assignments.filter(a => a.load_route_id === route.id);
          const routeAssigned = routeAssignments.reduce((sum, a) => sum + (a.vehicles_assigned || 0), 0);
          
          return {
            ...route,
            vehicles_assigned: routeAssigned
          };
        }) || [];
        
        console.log(`🔄 REALTIME: Processing order ${order.id} (${order.title}):`, {
          orderRouteIds,
          orderAssignmentsCount: orderAssignments.length,
          activeAssignmentsCount: activeAssignments.length,
          oldAssigned,
          totalAssigned,
          willUpdate: totalAssigned !== oldAssigned,
          routesUpdated: updatedRoutes.map(r => ({
            id: r.id,
            vehicles_assigned: r.vehicles_assigned
          }))
        });
        
        if (totalAssigned !== oldAssigned) {
          console.log(`🔄 REALTIME: Order ${order.id} trucks WILL BE UPDATED: ${oldAssigned} → ${totalAssigned}`);
        }
        
        return {
          ...order,
          vehicles_assigned: totalAssigned,
          load_routes: updatedRoutes
        };
      });
      
      console.log('🔄 REALTIME: Finished updateLoadOrderAggregates, returning updated orders');
      return updated;
    });
  };

  // Update activeAssignments whenever loadAssignments changes
  useEffect(() => {
    console.log('🔄 REACT: loadAssignments changed, filtering activeAssignments from', loadAssignments.length, 'total assignments');
    
    loadAssignments.forEach((a, index) => {
      const isAccepted = a.status === 'accepted';
      const hasVehicles = (a.vehicles_assigned || 0) > 0;
      const willBeActive = isAccepted && hasVehicles;
      
      console.log(`Assignment ${index + 1} (${a.carrier?.name}):`, {
        id: a.id,
        status: a.status,
        vehicles_assigned: a.vehicles_assigned,
        isAccepted,
        hasVehicles,
        willBeActive
      });
    });
    
    const active = loadAssignments.filter(a => 
      a.status === 'accepted' && (a.vehicles_assigned || 0) > 0
    );
    console.log('📋 REALTIME: activeAssignments updated:', {
      totalAssignments: loadAssignments.length,
      activeCount: active.length,
      activeDetails: active.map(a => ({
        id: a.id.slice(0, 8),
        vehicles: a.vehicles_assigned,
        carrier: a.carrier?.name
      }))
    });
    setActiveAssignments(active);
  }, [loadAssignments]);
  
  // Add useEffect to track loadOrders changes
  useEffect(() => {
    console.log('🔄 REACT: loadOrders state changed, count:', loadOrders.length);
    loadOrders.forEach((order, index) => {
      console.log(`Order ${index + 1} (${order.title}):`, {
        id: order.id,
        vehicles_assigned: order.vehicles_assigned,
        vehicles_needed: order.vehicles_needed
      });
    });
  }, [loadOrders]);

  // Load data from Supabase
  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  // Check for existing invitations and create default link
  useEffect(() => {
    console.log('🔗 Organization effect triggered:', { 
      user: !!user, 
      currentOrganization: !!currentOrganization,
      orgId: currentOrganization?.organization_id,
      hasOrganization,
      orgLoading
    });
    
    if (user && currentOrganization) {
      console.log('🔗 Organization available, checking invitation link for:', currentOrganization.organization_id);
      checkAndCreateDefaultInviteLink();
    } else if (user && hasOrganization && !currentOrganization && !orgLoading) {
      console.log('🔗 User has organization but currentOrganization not set, refreshing...');
      refreshOrganizations();
    }
  }, [user, currentOrganization, hasOrganization, orgLoading]);

  // Check for active connections in the current organization
  useEffect(() => {
    const checkActiveConnections = async () => {
      if (!currentOrganization) {
        setHasActiveConnections(false);
        return;
      }

      try {
        const { data: orgDetails, error } = await supabase
          .from('organizations')
          .select('active_connections_count')
          .eq('id', currentOrganization.organization_id)
          .single();

        if (error) {
          console.error('Error fetching organization details:', error);
          return;
        }

        const activeConnectionsCount = orgDetails?.active_connections_count || 0;
        setHasActiveConnections(activeConnectionsCount > 0);
        console.log('Active connections check:', { 
          orgId: currentOrganization.organization_id, 
          activeConnectionsCount, 
          hasActiveConnections: activeConnectionsCount > 0 
        });
      } catch (error) {
        console.error('Error checking active connections:', error);
      }
    };

    checkActiveConnections();
  }, [currentOrganization]);

  // Listen for navigate-to-carriers event from SettingsDropdown
  useEffect(() => {
    const handleNavigateToCarriers = () => {
      setActiveView("carriers");
    };

    window.addEventListener('navigate-to-carriers', handleNavigateToCarriers);
    
    return () => {
      window.removeEventListener('navigate-to-carriers', handleNavigateToCarriers);
    };
  }, []);

  // Set up realtime subscriptions for live updates
  useEffect(() => {
    if (!user) return;

    console.log('🔄 REALTIME: Setting up load_assignments subscription');
    
    const channel = supabase
      .channel('dashboard-load-assignments-updates')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'load_assignments'
      }, async (payload) => {
        console.log('🔄 REALTIME: Load assignment event received:', {
          event: payload.eventType,
          table: payload.table,
          id: (payload.new as any)?.id || (payload.old as any)?.id
        });
        
        // Always refetch fresh data to ensure consistency
        try {
          console.log('🔄 REALTIME: Starting data refetch...');
          
          // Get user organizations
          const { data: userOrgs } = await supabase.rpc('get_user_organizations', { 
            user_uuid: user.id 
          });
          
          console.log('🔄 REALTIME: User orgs:', userOrgs?.length || 0);
          
          if (!userOrgs || userOrgs.length === 0) {
            console.log('🔄 REALTIME: No user orgs found, skipping update');
            return;
          }
          
          const orgIds = userOrgs.map(org => org.organization_id);
          console.log('🔄 REALTIME: Org IDs:', orgIds);
          
          // Fetch fresh orders with updated vehicles_assigned values
          const { data: freshOrders, error: ordersError } = await supabase
            .from('load_orders')
            .select(`
              *,
              load_routes(
                id,
                title,
                pickup_city,
                delivery_city,
                status,
                pickup_date,
                delivery_date,
                vehicle_type,
                vehicles_needed,
                vehicles_assigned,
                vehicles_active
              )
            `)
            .in('customer_org_id', orgIds)
            .order('created_at', { ascending: false });
          
          if (ordersError) {
            console.error('🔄 REALTIME: Error fetching orders:', ordersError);
            return;
          }
          
          console.log('🔄 REALTIME: Fresh orders fetched:', freshOrders?.length || 0);
          
          if (freshOrders) {
            console.log('🔄 REALTIME: Updating orders with fresh data from database');
            setLoadOrders(freshOrders);
          }
          
          // Get all routes for user's orders
          const { data: userRoutes } = await supabase
            .from('load_routes')
            .select('id, load_order_id')
            .in('customer_org_id', orgIds);
          
          console.log('🔄 REALTIME: User routes:', userRoutes?.length || 0);
          
          if (!userRoutes || userRoutes.length === 0) {
            console.log('🔄 REALTIME: No user routes found, skipping update');
            return;
          }
          
          const routeIds = userRoutes.map(r => r.id);
          console.log('🔄 REALTIME: Route IDs:', routeIds);
          
          // Fetch fresh assignments with carrier info
          const { data: freshAssignments, error: assignmentsError } = await supabase
            .from('load_assignments')
            .select(`
              *,
              load_routes!fk_load_assignments_load_route_id(id, title, load_order_id)
            `)
            .in('load_route_id', routeIds)
            .order('created_at', { ascending: false });
          
          if (assignmentsError) {
            console.error('🔄 REALTIME: Error fetching assignments:', assignmentsError);
            return;
          }
          
          console.log('🔄 REALTIME: Fresh assignments fetched:', freshAssignments?.length || 0);
          
          if (freshAssignments) {
            // Get unique carrier org IDs to fetch carrier info separately
            const carrierOrgIds = [...new Set(
              freshAssignments
                .map(a => a.carrier_org_id)
                .filter(Boolean)
            )];
            
            // Fetch carrier information separately
            let carriersMap = new Map();
            if (carrierOrgIds.length > 0) {
              const { data: carriers } = await supabase
                .from('organizations')
                .select('id, name, username')
                .in('id', carrierOrgIds);
              
              if (carriers) {
                carriers.forEach(carrier => carriersMap.set(carrier.id, carrier));
              }
            }
            
            const formattedAssignments = freshAssignments.map((assignment: any) => ({
              ...assignment,
              carrier: carriersMap.get(assignment.carrier_org_id) || null,
              load_routes: assignment.load_routes
            }));
            
            console.log('🔄 REALTIME: Updating assignments from fresh data:', {
              event: payload.eventType,
              count: formattedAssignments.length,
              affectedId: (payload.new as any)?.id || (payload.old as any)?.id,
              sampleAssignment: formattedAssignments[0] ? {
                id: formattedAssignments[0].id,
                load_route_id: formattedAssignments[0].load_route_id,
                vehicles_assigned: formattedAssignments[0].vehicles_assigned,
                load_routes: formattedAssignments[0].load_routes
              } : null
            });
            
            console.log('🔄 REALTIME: About to update state...');
            setLoadAssignments(formattedAssignments);
            console.log('🔄 REALTIME: Load assignments state updated');
            
            // Show notification for accept/complete/cancel events
            if (payload.eventType === 'UPDATE' && payload.new && payload.old) {
              const oldAssignment = payload.old;
              const newAssignment = payload.new;
              
              // Check for truck completion
              if ((newAssignment.vehicles_completed || 0) > (oldAssignment.vehicles_completed || 0)) {
                const completedCount = (newAssignment.vehicles_completed || 0) - (oldAssignment.vehicles_completed || 0);
                toast({
                  title: t("dashboard.truckCompleted"),
                  description: `${completedCount} truck(s) marked as completed`,
                });
              }
              
              // Check for truck cancellation
              if ((newAssignment.vehicles_canceled || 0) > (oldAssignment.vehicles_canceled || 0)) {
                const canceledCount = (newAssignment.vehicles_canceled || 0) - (oldAssignment.vehicles_canceled || 0);
                toast({
                  title: t("dashboard.truckCanceled"),
                  description: `${canceledCount} truck(s) canceled`,
                  variant: "destructive"
                });
              }
              
              // Check for truck acceptance
              if ((newAssignment.vehicles_assigned || 0) > (oldAssignment.vehicles_assigned || 0)) {
                const acceptedCount = (newAssignment.vehicles_assigned || 0) - (oldAssignment.vehicles_assigned || 0);
                toast({
                  title: t("dashboard.truckAccepted"),
                  description: `${acceptedCount} truck(s) accepted by carrier`,
                });
              }
            }
          }
        } catch (error) {
          console.error('🔄 REALTIME: Error refreshing data:', error);
        }
      })
      .subscribe((status) => {
        console.log('🔄 REALTIME: Subscription status:', status);
      });

    setRealtimeChannel(channel);

    return () => {
      console.log('🔄 REALTIME: Cleaning up subscription');
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [user, toast, t]);

  // Check invitations and create default link if needed
  const checkAndCreateDefaultInviteLink = async () => {
    if (!user || !currentOrganization) {
      console.log('🔗 checkAndCreateDefaultInviteLink: Missing user or organization', { user: !!user, currentOrganization: !!currentOrganization });
      return;
    }
    
    console.log('🔗 checkAndCreateDefaultInviteLink: Starting check for org:', currentOrganization.organization_id);
    setLoadingInviteLink(true);
    
    try {
      // Check for active link invitations only
      const { data: linkInvitations, error: linkError } = await supabase
        .from('connection_invitations')
        .select('id, invitation_target')
        .eq('inviter_org_id', currentOrganization.organization_id)
        .eq('invitation_type', 'link')
        .eq('status', 'active')
        .limit(1);
      
      if (linkError) throw linkError;
      
      console.log('🔗 Query results:', { linkInvitations: linkInvitations?.length || 0 });
      
      const hasActiveLinkInvitation = linkInvitations && linkInvitations.length > 0;
      
      if (hasActiveLinkInvitation && linkInvitations[0]) {
        // If there's already an active link invitation, use it as the default
        const existingInviteLink = generateInviteLink(linkInvitations[0].id);
        console.log('🔗 Using existing invitation link:', existingInviteLink);
        setDefaultInviteLink(existingInviteLink);
      } else {
        // No active link invitation exists, create a new one
        console.log('🔗 Creating new invitation link...');
        try {
          const { data: newInvitation, error } = await supabase
            .from('connection_invitations')
            .insert({
              inviter_org_id: currentOrganization.organization_id,
              invited_by: user.id,
              invitation_type: 'link',
              invitation_target: 'generic_link',
              status: 'active'
            })
            .select('id')
            .single();

          if (error) {
            console.error('🔗 Error creating invitation:', error);
            throw error;
          }
          
          const inviteLink = generateInviteLink(newInvitation.id);
          console.log('🔗 Created new invitation link:', inviteLink);
          setDefaultInviteLink(inviteLink);
        } catch (error) {
          console.error('🔗 Error creating default invite link:', error);
        }
      }
    } catch (error) {
      console.error('🔗 Error checking invitations:', error);
    } finally {
      setLoadingInviteLink(false);
    }
  };

  const loadData = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      
      // Load orders for user's organizations
      const { data: userOrgs } = await supabase.rpc('get_user_organizations', { 
        user_uuid: user.id 
      });
      
      if (!userOrgs || userOrgs.length === 0) {
        setLoadOrders([]);
        setLoadAssignments([]);
        setLoading(false);
        return;
      }
      
      const orgIds = userOrgs.map(org => org.organization_id);
      
      const { data: requests, error: requestsError } = await supabase
        .from('load_orders')
        .select(`
          *,
          load_routes(
            id,
            title,
            pickup_city,
            delivery_city,
            status,
            pickup_date,
            delivery_date,
            vehicle_type,
            vehicles_needed,
            vehicles_assigned,
            vehicles_active
          )
        `)
        .in('customer_org_id', orgIds)
        .order('created_at', { ascending: false });

      if (requestsError) throw requestsError;

      // Track if user has ever created orders (regardless of filters)
      setHasEverCreatedOrders(requests && requests.length > 0);

      // Load assignments for these routes with carrier info
      const routeIds = requests?.flatMap(order => order.load_routes?.map(route => route.id) || []) || [];
      console.log('🔧 LOADDATA DEBUG:', {
        totalOrders: requests?.length || 0,
        routeIds: routeIds,
        routeIdsCount: routeIds.length
      });
      
      let assignments = [];
      if (routeIds.length > 0) {
        try {
          console.log('🔧 Querying assignments for route IDs:', routeIds);
          const { data: assignmentData, error: assignmentsError } = await supabase
            .from('load_assignments')
            .select(`
              *,
              vehicles_accepted,
              load_routes!fk_load_assignments_load_route_id(id, title, load_order_id)
            `)
            .in('load_route_id', routeIds);

          console.log('🔧 Assignment query result:', {
            error: assignmentsError,
            dataCount: assignmentData?.length || 0,
            rawData: assignmentData
          });
          
          // Log detailed structure of each assignment
          assignmentData?.forEach((assignment, index) => {
            console.log(`🔧 Assignment ${index + 1}:`, {
              id: assignment.id,
              carrier_org_id: assignment.carrier_org_id,
              load_route_id: assignment.load_route_id,
              vehicles_assigned: assignment.vehicles_assigned,
              vehicles_accepted: assignment.vehicles_accepted,
              status: assignment.status,
              load_routes: assignment.load_routes,
              fullAssignment: assignment
            });
          });

          if (assignmentsError) {
            console.error('Assignments error:', assignmentsError);
            // Don't throw, just continue with empty assignments
          } else {
            assignments = assignmentData || [];
            
            // Fetch carrier information separately for each assignment
            if (assignments.length > 0) {
              console.log('🔧 Fetching carrier info for', assignments.length, 'assignments');
              const carrierIds = [...new Set(assignments.map(a => a.carrier_org_id).filter(Boolean))];
              
              if (carrierIds.length > 0) {
                const { data: carriers } = await supabase
                  .from('organizations')
                  .select('id, name, username')
                  .in('id', carrierIds);
                
                console.log('🔧 Fetched carriers:', carriers);
                
                // Attach carrier info to assignments
                assignments = assignments.map(assignment => ({
                  ...assignment,
                  carrier: carriers?.find(c => c.id === assignment.carrier_org_id) || null
                }));
              }
            }
          }
        } catch (assignmentError) {
          console.error('Failed to load assignments:', assignmentError);
          // Continue with empty assignments
        }
      } else {
        console.log('🔧 No route IDs found, skipping assignment query');
      }

      console.log('🔧 Final assignments to set:', assignments.length);
      setLoadOrders(requests || []);
      setLoadAssignments(assignments);
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: "Error loading data",
        description: "Failed to load your load requests",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLoadConfirmed = async (loadRequestData: any) => {
    if (!user) {
      throw new Error("You must be logged in to create load requests");
    }

    // Check if user has an organization
    const { data: userOrgs } = await supabase.rpc('get_user_organizations', { 
      user_uuid: user.id 
    });
    
    if (!userOrgs || userOrgs.length === 0) {
      throw new Error("Please create or join an organization first");
    }
    
    // Use the first organization if none is selected
    const orgToUse = currentOrganization?.organization_id || userOrgs[0].organization_id;
    
    // First create the load order
    const { data: loadOrder, error: orderError } = await supabase
      .from('load_orders')
      .insert({
        created_by: user.id,
        customer_org_id: orgToUse,
        title: loadRequestData.title || 'Load Order',
        description: loadRequestData.description || `Load order with ${loadRequestData.cargo_desc || 'cargo'}`,
        cargo_type: loadRequestData.cargo_type || 'general',
        cargo_desc: loadRequestData.cargo_desc,
        cargo_weight: loadRequestData.cargo_weight,
        cargo_value: null,
        vehicles_needed: loadRequestData.vehicles_needed || 1,
        vehicles_assigned: 0,
        vehicles_active: 0,
        vehicles_completed: 0,
        vehicles_canceled: 0,
        status: 'published'
      })
      .select()
      .single();

    if (orderError) throw orderError;

    // Check if this is a multi-route request or single route
    if (loadRequestData.routes && Array.isArray(loadRequestData.routes)) {
      // Create multiple routes for this order
      const routesToInsert = loadRequestData.routes.map((route: any) => ({
        load_order_id: loadOrder.id,
        customer_org_id: orgToUse,
        title: route.title,
        pickup_address: route.pickup_address,
        pickup_city: route.pickup_city,
        pickup_state: route.pickup_state,
        pickup_country: route.pickup_country || 'Saudi Arabia',
        pickup_date: route.pickup_date,
        delivery_address: route.delivery_address,
        delivery_city: route.delivery_city,
        delivery_state: route.delivery_state,
        delivery_country: route.delivery_country || 'Saudi Arabia',
        delivery_date: route.delivery_date,
        vehicle_type: route.vehicle_type || 'truck',
        vehicles_needed: route.vehicles_needed || 1,
        vehicles_assigned: 0,
        vehicles_active: 0,
        vehicles_completed: 0,
        vehicles_canceled: 0,
        status: 'published'
      }));

      const { error: routesError } = await supabase
        .from('load_routes')
        .insert(routesToInsert);

      if (routesError) throw routesError;
    } else {
      // Create single route (backward compatibility)
      const { error: routeError } = await supabase
        .from('load_routes')
        .insert({
          load_order_id: loadOrder.id,
          customer_org_id: orgToUse,
          title: loadRequestData.title || 'Route 1',
          pickup_address: loadRequestData.pickup_address,
          pickup_city: loadRequestData.pickup_city,
          pickup_state: loadRequestData.pickup_state,
          pickup_country: loadRequestData.pickup_country || 'Saudi Arabia',
          pickup_date: loadRequestData.pickup_date,
          delivery_address: loadRequestData.delivery_address,
          delivery_city: loadRequestData.delivery_city,
          delivery_state: loadRequestData.delivery_state,
          delivery_country: loadRequestData.delivery_country || 'Saudi Arabia',
          delivery_date: loadRequestData.delivery_date,
          vehicle_type: loadRequestData.vehicle_type || 'truck',
          vehicles_needed: loadRequestData.vehicles_needed || 1,
          vehicles_assigned: 0,
          vehicles_active: 0,
          vehicles_completed: 0,
          vehicles_canceled: 0,
          status: 'published'
        });

      if (routeError) throw routeError;
    }

    // Notification will be automatically queued by database trigger

    // Reload data
    await loadData();
    setActiveView("dashboard");
  };

  const handleShareLoad = async (loadId: string) => {
    const shareUrl = `${window.location.origin}/load/${loadId}`;
    
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast({
        title: t('toast.linkCopied'),
        description: t('toast.linkCopiedDescription'),
      });
    } catch (err) {
      toast({
        title: t('toast.failedToCopy'),
        description: t('toast.failedToCopyDescription'),
        variant: "destructive",
      });
    }
  };

  const cancelLoad = async (loadId: string) => {
    try {
      const { error } = await supabase
        .from('load_orders')
        .update({ status: 'canceled' })
        .eq('id', loadId);

      if (error) throw error;

      toast({
        title: "Load cancelled",
        description: "Your load order has been cancelled"
      });

      // Reload data
      loadData();
    } catch (error) {
      console.error('Error cancelling load:', error);
      toast({
        title: "Error cancelling load",
        description: "Failed to cancel your load request",
        variant: "destructive"
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "unpublished": return "secondary";
      case "published": return "warning";
      case "assigned": return "default";
      case "completed": return "success";
      case "canceled": return "destructive";
      default: return "secondary";
    }
  };

  const getAssignmentsForRoute = (routeId: string) => {
    return loadAssignments.filter(assignment => assignment.load_route_id === routeId);
  };

  const handleRouteClick = (route: LoadRoute) => {
    setSelectedRoute(route);
    setIsRouteDialogOpen(true);
  };

  const getCarrierGroupsForOrder = useMemo(() => {
    console.log('🔧 useMemo triggered, loadAssignments.length:', loadAssignments.length);
    
    return (orderId: string) => {
      console.log('🔧 getCarrierGroupsForOrder called for orderId:', orderId);
      const allOrderAssignments = getAssignmentsForOrder(orderId);
      console.log('🔧 allOrderAssignments length:', allOrderAssignments.length);
      
      const carrierGroups = allOrderAssignments.reduce((groups, assignment, index) => {
        const carrierId = assignment.carrier_org_id || 'unknown';
        console.log(`🔧 Processing assignment ${index + 1}: ${assignment.carrier?.name} - ${assignment.vehicles_assigned} vehicles`);
        
        if (!groups[carrierId]) {
          groups[carrierId] = {
            carrier: assignment.carrier,
            assignments: [],
            assignedVehicles: 0,
            canceledVehicles: 0,
            activeVehicles: 0,
            completedVehicles: 0,
            acceptedVehicles: 0
          };
        }
        groups[carrierId].assignedVehicles += (assignment.vehicles_assigned || 0);
        groups[carrierId].canceledVehicles += (assignment.vehicles_canceled || 0);
        groups[carrierId].activeVehicles += (assignment.vehicles_active || 0);
        groups[carrierId].completedVehicles += (assignment.vehicles_completed || 0);
        groups[carrierId].acceptedVehicles += (assignment.vehicles_accepted || 0);
        return groups;
      }, {} as Record<string, { 
        carrier: any, 
        assignments: LoadAssignment[], 
        assignedVehicles: number,
        canceledVehicles: number,
        activeVehicles: number,
        completedVehicles: number,
        acceptedVehicles: number
      }>);
      
      console.log(`🧮 Final carrier groups:`, 
        Object.entries(carrierGroups).map(([carrierId, group]) => ({
          carrierName: group.carrier?.name || 'Unknown',
          assignmentCount: group.assignments.length,
          assignedVehicles: group.assignedVehicles
        }))
      );
      
      return carrierGroups;
    };
  }, [loadAssignments]); // Re-calculate when loadAssignments changes

  const getAssignmentsForOrder = (orderId: string) => {
    console.log(`🔍 FILTERING assignments for order ${orderId}:`);
    loadAssignments.forEach((assignment, index) => {
      const hasLoadRoutes = !!assignment.load_routes;
      const loadOrderId = assignment.load_routes?.load_order_id;
      const matches = hasLoadRoutes && loadOrderId === orderId;
      
      console.log(`Assignment ${index + 1}:`, {
        id: assignment.id,
        hasLoadRoutes,
        loadOrderId,
        targetOrderId: orderId,
        matches,
        loadRoutes: assignment.load_routes
      });
    });
    
    const result = loadAssignments.filter(assignment => 
      assignment.load_routes && assignment.load_routes.load_order_id === orderId
    );
    
    console.log(`🚨 FILTER RESULT MISMATCH:`, {
      expectedToMatch: 9, 
      actualMatches: result.length,
      missingAssignments: loadAssignments.length - result.length,
      allShouldMatch: loadAssignments.every(a => a.load_routes && a.load_routes.load_order_id === orderId)
    });
    console.log(`🔍 getAssignmentsForOrder(${orderId}):`, {
      totalLoadAssignments: loadAssignments.length,
      filteredResult: result.length,
      loadAssignments: loadAssignments.map(a => ({
        id: a.id,
        load_route_id: a.load_route_id,
        load_routes: a.load_routes,
        vehicles_assigned: a.vehicles_assigned,
        carrier: a.carrier
      })),
      result: result
    });
    return result;
  };

  const getCarrierRoutes = (carrierOrgId: string, filterByLoadOrderId?: string) => {
    const carrierAssignments = loadAssignments.filter(a => a.carrier_org_id === carrierOrgId);
    const routeIds = [...new Set(carrierAssignments.map(a => a.load_route_id))]; // Get unique route IDs
    
    const carrierRoutes = [];
    
    // Filter orders by the specific load order ID if provided
    const ordersToCheck = filterByLoadOrderId 
      ? loadOrders.filter(order => order.id === filterByLoadOrderId)
      : loadOrders;
    
    for (const order of ordersToCheck) {
      for (const route of order.load_routes || []) {
        if (routeIds.includes(route.id)) {
          // Get ALL assignments for this carrier on this route and sum their vehicles
          const routeAssignments = carrierAssignments.filter(a => a.load_route_id === route.id);
          const assignedVehicles = routeAssignments.reduce((sum, a) => sum + (a.vehicles_assigned || 0), 0);
          
          carrierRoutes.push({
            ...route,
            assignment: routeAssignments[0], // Keep for compatibility
            assignments: routeAssignments, // All assignments
            assignedVehicles, // Sum of all vehicles
            orderTitle: order.title
          });
        }
      }
    }
    return carrierRoutes;
  };

  const pendingOrders = loadOrders.filter(l => l.status === "unpublished" || l.status === "published");
  const assignedOrders = loadOrders.filter(l => (l.vehicles_needed || 0) - (l.vehicles_assigned || 0) > 0);
  const deliveredOrders = loadOrders.filter(l => (l.vehicles_needed || 0) - (l.vehicles_assigned || 0) === 0);
  const cancelledOrders = loadOrders.filter(l => l.status === "canceled");

  // Filter orders based on current filter
  const filteredOrders = orderFilter ? loadOrders.filter(l => {
    if (orderFilter === "assigned") {
      return (l.vehicles_needed || 0) - (l.vehicles_assigned || 0) > 0;
    }
    if (orderFilter === "completed") {
      return (l.vehicles_needed || 0) - (l.vehicles_assigned || 0) === 0;
    }
    return l.status === orderFilter;
  }) : loadOrders;

  if (activeView === "new-load") {
    return (
      <div className="space-y-4">
        <div className={`flex items-center gap-4 p-4 ${isRTL ? 'justify-end' : 'justify-start'}`}>
          <Button variant="outline" onClick={() => setActiveView("dashboard")}>
            {t('dashboard.backToDashboard')}
          </Button>
        </div>
        <LoadRequestChat companyName={companyName} onLoadConfirmed={handleLoadConfirmed} />
      </div>
    );
  }

  if (activeView === "carriers") {
    return (
      <div className="space-y-4">
        <div className={`flex items-center gap-4 p-4 ${isRTL ? 'justify-end' : 'justify-start'}`}>
          <Button variant="outline" onClick={() => setActiveView("dashboard")}>
            {t('dashboard.backToDashboard')}
          </Button>
        </div>
        <CarrierManagement companyName={companyName} />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className={`relative min-h-screen ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-6xl mx-auto p-3 md:p-4 space-y-4 md:space-y-6 pb-20 md:pb-4">
        {/* Header */}
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-4">
          {isRTL ? (
            <>
              {/* Logo section - right side in RTL */}
              <div className="flex items-center gap-2 sm:gap-4 flex-row-reverse order-1 sm:order-2">
                <div className="min-w-0 flex-1">
                  <h1 className="text-lg sm:text-2xl lg:text-3xl font-bold text-foreground truncate text-right">{t('dashboard.title')}</h1>
                  <p className="text-xs sm:text-base text-muted-foreground truncate text-right">
                    <span dir="ltr">@{companyName.charAt(0).toUpperCase() + companyName.slice(1)}</span> • {t('platform.subtitle')}
                  </p>
                </div>
                <div className="bg-primary px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg flex-shrink-0">
                  <img src="/lovable-uploads/94433138-910b-4d0f-a142-73e70d167ac6.png" alt="Shohnat" className="h-6 sm:h-6 w-auto" />
                </div>
              </div>
              
              {/* Buttons section - left side in RTL */}
              <div className="flex gap-2 order-2 sm:order-1 w-full sm:w-auto">
                {user && (
                  <Button 
                    onClick={() => navigate('/new-load')} 
                    className="hidden md:flex flex-1 sm:flex-initial h-9 sm:h-10 px-3 sm:px-4 hover-scale transition-all duration-200 text-xs sm:text-sm"
                    variant="accent"
                  >
                  <Plus className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                    <span className="hidden xs:inline">{t("dashboard.newLoadRequest")}</span>
                    <span className="xs:hidden">{t("dashboard.newLoad")}</span>
                  </Button>
                )}
                <SettingsDropdown />
              </div>
            </>
          ) : (
            <>
              {/* Logo section - left side in LTR */}
              <div className="flex items-center gap-2 sm:gap-4 order-1">
                <div className="bg-primary px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg flex-shrink-0">
                  <img src="/lovable-uploads/94433138-910b-4d0f-a142-73e70d167ac6.png" alt="Shohnat" className="h-4 sm:h-6 w-auto" />
                </div>
                <div className="min-w-0 flex-1">
                  <h1 className="text-lg sm:text-2xl lg:text-3xl font-bold text-foreground truncate text-left">{t('dashboard.title')}</h1>
                  <p className="text-xs sm:text-base text-muted-foreground truncate text-left">{companyName} • {t('dashboard.subtitle')}</p>
                </div>
              </div>
              
              {/* Buttons section - right side in LTR */}
              <div className="flex gap-2 order-2">
                {user && (
                  <Button 
                    onClick={() => navigate('/new-load')} 
                    className="hidden md:flex flex-1 sm:flex-initial h-9 sm:h-10 px-3 sm:px-4 hover-scale transition-all duration-200 text-xs sm:text-sm"
                    variant="accent"
                  >
                  <Plus className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                    <span className="hidden xs:inline">{t("dashboard.newLoadRequest")}</span>
                    <span className="xs:hidden">{t("dashboard.newLoad")}</span>
                  </Button>
                )}
                <SettingsDropdown />
              </div>
            </>
          )}
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <Card 
            className={`cursor-pointer transition-all duration-200 ${
              orderFilter === "assigned" ? "bg-navy-blue text-white shadow-xl" : "hover:shadow-md bg-card"
            }`}
            onClick={() => setOrderFilter(orderFilter === "assigned" ? null : "assigned")}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold mb-1">{assignedOrders.length}</p>
                  <p className="text-sm opacity-90">{t('stats.active')}</p>
                </div>
                <div className={`p-3 rounded-full ${
                  orderFilter === "assigned" ? "bg-white/20" : "bg-muted"
                }`}>
                  <Truck className={`h-6 w-6 ${
                    orderFilter === "assigned" ? "text-white" : "text-muted-foreground"
                  }`} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className={`cursor-pointer transition-all duration-200 ${
              orderFilter === "completed" ? "bg-green-600 text-white shadow-xl" : "hover:shadow-md bg-card"
            }`}
            onClick={() => setOrderFilter(orderFilter === "completed" ? null : "completed")}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold mb-1">{deliveredOrders.length}</p>
                  <p className="text-sm opacity-90">{t('stats.completed')}</p>
                </div>
                <div className={`p-3 rounded-full ${
                  orderFilter === "completed" ? "bg-white/20" : "bg-muted"
                }`}>
                  <CheckCircle className={`h-6 w-6 ${
                    orderFilter === "completed" ? "text-white" : "text-muted-foreground"
                  }`} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>


        {/* Load Requests */}
        <div className="space-y-4">
          <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
            <h2 className="text-lg sm:text-xl font-semibold text-foreground">
              {t('loadOrders.loadRequests')}
            </h2>
            <Button variant="outline" size="sm" onClick={loadData} className="hidden sm:flex">
              <RefreshCw className="h-4 w-4 mr-2" />
              {t('dashboard.refresh')}
            </Button>
          </div>

          {/* Show carrier invitation when orders exist but no active connections */}
          {loadOrders.length > 0 && !hasActiveConnections && (
            <Card className="border-blue-200 bg-blue-50/50 dark:bg-blue-950/20">
              <CardContent className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Users className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="flex-1 space-y-3 sm:space-y-4">
                    <h3 className="font-medium text-foreground text-sm sm:text-base">
                      {t('dashboard.inviteCarriers.title')}
                    </h3>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      {t('dashboard.inviteCarriers.description')}
                    </p>
                    
                    {/* Invitation Link */}
                    {defaultInviteLink && (
                      <div className="p-2 sm:p-3 bg-background rounded-md border">
                        <p className="text-xs text-muted-foreground mb-2">
                          {t('dashboard.inviteCarriers.shareLink')}
                        </p>
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                          <code className="flex-1 text-xs bg-muted p-2 rounded border font-mono overflow-hidden whitespace-nowrap">
                            {defaultInviteLink}
                          </code>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              navigator.clipboard.writeText(defaultInviteLink);
                              toast({
                                title: t('dashboard.inviteCarriers.linkCopied'),
                                description: t('dashboard.inviteCarriers.linkCopiedDesc'),
                              });
                            }}
                            className="h-8 px-3 w-full sm:w-auto"
                          >
                            <Copy className="h-3 w-3 mr-1" />
                            {t('dashboard.inviteCarriers.copy')}
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    <Button onClick={() => setIsInviteModalOpen(true)} variant="default" size="sm" className="w-full sm:w-auto">
                      <UserPlus className="h-4 w-4 mr-2" />
                      {t('dashboard.inviteCarriers.inviteButton')}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {!hasEverCreatedOrders ? (
            <Card>
              <CardContent className="p-8 text-center">
                <div className="space-y-6">
                  <div>
                    <Truck className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                    <h3 className="text-lg font-medium text-foreground mb-2">
                      {t('dashboard.getStarted.title')}
                    </h3>
                    <p className="text-muted-foreground">
                      {t('dashboard.getStarted.description')}
                    </p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4 max-w-2xl mx-auto">
                    {/* Step 1: Invite Carriers */}
                    <Card className={`relative border-2 transition-colors ${hasActiveConnections ? 'border-green-500 bg-green-50/50 dark:bg-green-950/20' : 'border-dashed hover:border-primary/50'}`}>
                      <CardContent className="p-6 text-center flex flex-col justify-between h-full">
                        <div>
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-3 text-sm font-semibold ${hasActiveConnections ? 'bg-green-500 text-white' : 'bg-primary text-primary-foreground'}`}>
                            {hasActiveConnections ? <CheckCircle className="h-4 w-4" /> : '1'}
                          </div>
                          <Users className={`h-8 w-8 mx-auto mb-3 ${hasActiveConnections ? 'text-green-500' : 'text-primary'}`} />
                          <h4 className="font-medium mb-2">{t('dashboard.getStarted.step1.title')}</h4>
                          <p className="text-sm text-muted-foreground mb-4">
                            {hasActiveConnections ? t('dashboard.getStarted.step1.completed') : t('dashboard.getStarted.step1.incomplete')}
                          </p>
                          
                          
                          {/* Quick copy invitation link */}
                          <div className="flex items-center gap-2 mb-2 px-2 py-1 bg-muted/50 rounded">
                            {loadingInviteLink ? (
                              <span className="flex-1 text-xs text-muted-foreground">
                                {isRTL ? 'انتظر... جاري تجهيز رابط الدعوة' : 'Preparing invitation link...'}
                              </span>
                            ) : defaultInviteLink ? (
                              <>
                                <code className="flex-1 text-xs font-mono text-left">{defaultInviteLink.replace('http://', 'https://').substring(0, 35)}...</code>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    navigator.clipboard.writeText(defaultInviteLink);
                                    toast({
                                      title: "Link copied!",
                                      description: "Invitation link copied to clipboard",
                                    });
                                  }}
                                  className="h-6 w-6 p-0 shrink-0"
                                >
                                  <Copy className="h-3 w-3" />
                                </Button>
                              </>
                            ) : orgLoading || !currentOrganization ? (
                              <span className="flex-1 text-xs text-muted-foreground">
                                {isRTL ? 'انتظر... جاري تحديث البيانات' : 'Loading organization data...'}
                              </span>
                            ) : (
                              <span className="flex-1 text-xs text-muted-foreground">
                                {isRTL ? 'خطأ في تحميل رابط الدعوة' : 'Error loading invitation link'}
                              </span>
                            )}
                          </div>
                          
                        </div>
                        
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setIsInviteModalOpen(true)}
                          className="w-full"
                          disabled={loadingInviteLink}
                        >
                          <UserPlus className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                          {loadingInviteLink ? 'Creating link...' : t('dashboard.getStarted.step1.action')}
                        </Button>
                      </CardContent>
                    </Card>

                    {/* Step 2: Place First Load */}
                    <Card className="relative border-dashed border-2 hover:border-primary/50 transition-colors">
                      <CardContent className="p-6 text-center flex flex-col justify-between h-full">
                        <div>
                          <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto mb-3 text-sm font-semibold">
                            2
                          </div>
                          <Package className="h-8 w-8 mx-auto mb-3 text-primary" />
                          <h4 className="font-medium mb-2">{t('dashboard.getStarted.step2.title')}</h4>
                          <p className="text-sm text-muted-foreground mb-4">
                            {t('dashboard.getStarted.step2.description')}
                          </p>
                        </div>
                        
                        <Button 
                          onClick={() => navigate('/new-load')}
                          variant="accent"
                          className="w-full"
                        >
                          <Plus className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                          {t('dashboard.getStarted.step2.action')}
                        </Button>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : filteredOrders.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <div className="space-y-4">
                  <Package className="h-12 w-12 mx-auto text-muted-foreground opacity-50" />
                  <div>
                    <h3 className="text-lg font-medium text-foreground mb-2">
                      {t('dashboard.noOrders.title')}
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      {orderFilter 
                        ? t('dashboard.noOrders.filtered')
                        : t('dashboard.noOrders.description')
                      }
                    </p>
                  </div>
                  <div className="flex justify-center">
                    <Button onClick={() => navigate('/new-load')} variant="accent" className="flex items-center gap-2">
                      <Plus className="h-4 w-4" />
                      {t('dashboard.newLoad')}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4 animate-fade-in" key={`orders-${loadAssignments.length}`}>
              {filteredOrders.map((order) => {
                // Calculate assignments normally - the key prop will handle re-renders
                const assignments = getAssignmentsForOrder(order.id);
                console.log(`🚨 BEFORE FILTER - Order ${order.id}:`, assignments.map(a => ({
                  id: a.id, 
                  vehicles_assigned: a.vehicles_assigned, 
                  type: typeof a.vehicles_assigned,
                  willPassFilter: (a.vehicles_assigned || 0) > 0
                })));
                // Use filtered activeAssignments for this order from the global state
                const orderRouteIds = order.load_routes?.map(route => route.id) || [];
                const orderActiveAssignments = activeAssignments.filter(a => orderRouteIds.includes(a.load_route_id));
                console.log(`🚨 AFTER FILTER - Order ${order.id}:`, orderActiveAssignments.length, 'active assignments');
                
                console.log(`📊 Order ${order.id} DEBUG:`, {
                  orderInfo: {
                    id: order.id,
                    title: order.title,
                    vehicles_needed: order.vehicles_needed,
                    vehicles_assigned: order.vehicles_assigned
                  },
                  totalAssignments: assignments.length,
                  activeAssignments: orderActiveAssignments.length,
                  allLoadAssignments: loadAssignments.length,
                  assignmentDetails: orderActiveAssignments.map(a => ({
                    id: a.id,
                    carrier_org_id: a.carrier_org_id,
                    vehicles_assigned: a.vehicles_assigned,
                    status: a.status,
                    carrier_info: a.carrier,
                    load_routes: a.load_routes
                  }))
                });
                
                return (
                  <Card 
                    key={order.id} 
                    className={`
                      shadow-[var(--shadow-card)] transition-all duration-300 ease-out relative
                      ${animatingOrders.has(order.id) 
                        ? "animate-bounce-green" 
                        : ""
                      }
                      ${(() => {
                        const availableTrucks = (order.vehicles_needed || 0) - (order.vehicles_assigned || 0);
                        const isSearching = (order.status === "unpublished" || order.status === "published") && availableTrucks > 0;
                        const isAssignedNoAvailable = availableTrucks === 0 && (order.vehicles_active || 0) > 0;
                        const isCompleted = availableTrucks === 0 && (order.vehicles_active || 0) === 0;
                        
                        if (isSearching) {
                          return "border-orange-500/40 bg-orange-50/50 dark:bg-orange-950/20";
                        } else if (isAssignedNoAvailable) {
                          return "border-blue-500/40 bg-blue-50/50 dark:bg-blue-950/20";
                        } else if (isCompleted) {
                          return "border-green-500/40 bg-green-50/50 dark:bg-green-950/20";
                        }
                        return "";
                      })()}
                    `}
                  >
                    {animatingOrders.has(order.id) && (
                      <div 
                        className="absolute inset-0 pointer-events-none"
                        style={{
                          background: `conic-gradient(from 0deg at 50% 50%, 
                            transparent 0deg, 
                            hsl(var(--primary)) 45deg, 
                            transparent 90deg, 
                            transparent 360deg)`,
                          mask: `linear-gradient(white, white) padding-box, 
                            linear-gradient(white, white)`,
                          maskComposite: 'xor',
                          WebkitMask: `linear-gradient(white, white) padding-box, 
                            linear-gradient(white, white)`,
                          WebkitMaskComposite: 'xor',
                          animation: 'spin 2s linear infinite'
                        }}
                      />
                    )}
                    <CardHeader className="pb-3 p-3 sm:p-6 sm:pb-3">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <CardTitle className="text-base sm:text-lg flex flex-wrap items-center gap-2">
                          <Truck className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                          <span className="font-mono text-sm sm:text-base">{order.id.slice(0, 8)}...</span>
                          <span className="text-sm font-normal">
                            {(order.status === "unpublished" || order.status === "published") && 
                             ((order.vehicles_needed || 0) - (order.vehicles_assigned || 0) > 0) && (
                              <span className="inline-flex items-center gap-2 text-warning">
                                <RefreshCw className="h-4 w-4 animate-spin" />
                                <span className="text-base font-medium">Searching for carriers</span>
                              </span>
                            )}
                          </span>
                        </CardTitle>
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant={getStatusColor(order.status) as any} className="text-xs h-8 px-3 border rounded-md">
                            {order.status === 'unpublished' ? 'Draft' : order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                          </Badge>
                          {(order.status === "unpublished" || order.status === "published") && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 px-3 gap-1 border rounded-md"
                                >
                                  <Menu className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-40">
                                <DropdownMenuItem onClick={() => handleShareLoad(order.id)}>
                                  <Share2 className="h-4 w-4 ltr:mr-2 rtl:ml-2" />
                                  {t('loadOrders.shareLoad')}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                                      <X className="h-4 w-4 ltr:mr-2 rtl:ml-2" />
                                      {t('loadOrders.cancelOrder')}
                                    </DropdownMenuItem>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent className="mx-4 sm:mx-0">
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Cancel Load Order</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        Are you sure you want to cancel this load order? This action cannot be undone.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                                      <AlertDialogCancel className="w-full sm:w-auto">Cancel</AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() => cancelLoad(order.id)}
                                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90 w-full sm:w-auto"
                                      >
                                        Cancel Order
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    
                     <CardContent className="space-y-4 p-3 sm:p-4 pt-0">
                        {/* Date/Time */}
                        <div className="flex items-center gap-2 text-sm text-foreground">
                          <Calendar className="w-4 h-4" />
                          <span className="font-medium">
                            {order.load_routes?.[0]?.pickup_date 
                              ? formatAppDate(order.load_routes[0].pickup_date, language) 
                              : 'Date TBD'}
                          </span>
                        </div>

                       {/* Truck Capacity */}
                       <div>
                         <div className="flex items-center justify-between mb-2">
                           <h4 className="text-sm font-medium text-muted-foreground">{t('trucks.confirmedTrucks')}</h4>
                            <span className="text-sm font-medium text-foreground">
                              {(() => {
                                // Use the aggregated value from load_orders table
                                const totalAssigned = order.vehicles_assigned || 0;
                                const totalNeeded = order.vehicles_needed || 1;
                                console.log(`Order ${order.id} - DB values: vehicles_assigned=${order.vehicles_assigned}, vehicles_needed=${order.vehicles_needed}`);
                                console.log(`Order ${order.id} - Calculated: totalAssigned=${totalAssigned}, totalNeeded=${totalNeeded}`);
                                return `${totalAssigned} / ${totalNeeded}`;
                              })()}
                            </span>
                         </div>
                          <div className="w-full bg-muted rounded-full h-2">
                            <div 
                              className="h-2 rounded-full bg-gradient-accent transition-all duration-500 ease-out"
                               style={{ 
                                 width: `${(() => {
                                   const totalAssigned = order.vehicles_assigned || 0;
                                  const totalNeeded = order.vehicles_needed || 1;
                                  return Math.min(100, (totalAssigned / totalNeeded) * 100);
                                })()}%` 
                              }}
                           ></div>
                         </div>
                       </div>

                       {/* Routes */}
                       <div className="space-y-2">
                         <p className="text-sm font-medium text-foreground">{t('routes.routes')} ({order.load_routes?.length || 0}):</p>
                         <div className="space-y-2">
                             {order.load_routes?.map((route, index) => {
                               // Use the vehicles_assigned value that's already calculated and updated in real-time
                               const acceptedVehicles = route.vehicles_assigned || 0;
                               const totalNeeded = route.vehicles_needed || 1;
                               const progress = acceptedVehicles / totalNeeded;
                               
                               console.log(`🚛 ROUTE DISPLAY: Route ${route.id} (${route.pickup_city} → ${route.delivery_city}):`, {
                                 acceptedVehicles,
                                 totalNeeded,
                                 progress: `${Math.round(progress * 100)}%`
                               });
                              
                              return (
                                 <div 
                                   key={route.id} 
                                   className="flex items-center justify-between cursor-pointer bg-muted/50 hover:bg-muted/70 p-3 rounded-lg transition-colors"
                                   onClick={() => handleRouteClick(route)}
                                 >
                                   <div className="flex items-center gap-2">
                                     <MapPin className="w-4 h-4 text-muted-foreground" />
                                     <span className="text-sm">{formatRoute(route.pickup_city, route.delivery_city)}</span>
                                   </div>
                                   <div className="flex items-center gap-2">
                                      <div className="w-16 h-1.5 bg-muted rounded-full">
                                        <div 
                                          className="h-1.5 rounded-full bg-green-500 transition-all duration-500 ease-out"
                                          style={{ width: `${Math.min(100, progress * 100)}%` }}
                                        ></div>
                                     </div>
                                     <span className="text-sm font-medium text-muted-foreground">
                                       {acceptedVehicles}/{totalNeeded}
                                     </span>
                                     <ChevronRight className={`w-4 h-4 text-muted-foreground ${isRTL ? 'rotate-180' : ''}`} />
                                   </div>
                                 </div>
                              );
                           })}
                         </div>
                       </div>

                          {/* Accepted Carriers Section */}
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm">
                              <span className="font-medium text-foreground">Accepted Carriers</span>
                            </div>
                            {(() => {
                              const allOrderAssignments = getAssignmentsForOrder(order.id);
                              
                              const totalAssignedVehicles = allOrderAssignments.reduce((sum, a) => sum + (a.vehicles_assigned || 0), 0);
                              
                              console.log(`🎯 GREEN CIRCLE DEBUG for order ${order.id}:`, {
                                totalAssignments: allOrderAssignments.length,
                                calculatedTotal: totalAssignedVehicles,
                                assignmentsData: allOrderAssignments.map(a => ({
                                  id: a.id,
                                  carrier: a.carrier?.name,
                                  vehicles_assigned: a.vehicles_assigned,
                                  vehicles_accepted: a.vehicles_accepted,
                                  status: a.status
                                })),
                                timestamp: new Date().toISOString()
                              });
                              
                              return allOrderAssignments.length === 0 ? (
                                <p className="text-sm text-muted-foreground">No carriers assigned yet</p>
                              ) : (
                                <div className="flex flex-wrap gap-3">
                                  {(() => {
                                   const carrierGroups = allOrderAssignments.reduce((groups, assignment) => {
                                     const carrierId = assignment.carrier_org_id || 'unknown';
                                     if (!groups[carrierId]) {
                                       groups[carrierId] = {
                                         carrier: assignment.carrier,
                                         assignments: [],
                                         assignedVehicles: 0,
                                         canceledVehicles: 0,
                                         activeVehicles: 0,
                                         completedVehicles: 0,
                                         acceptedVehicles: 0
                                       };
                                     }
                                       groups[carrierId].assignments.push(assignment);
                                       groups[carrierId].assignedVehicles += (assignment.vehicles_assigned || 0);
                                      groups[carrierId].canceledVehicles += (assignment.vehicles_canceled || 0);
                                      groups[carrierId].activeVehicles += (assignment.vehicles_active || 0);
                                      groups[carrierId].completedVehicles += (assignment.vehicles_completed || 0);
                                      groups[carrierId].acceptedVehicles += (assignment.vehicles_accepted || 0);
                                      
                                      console.log(`🎯 CARRIER GROUP DEBUG: Adding assignment ${assignment.id} to carrier ${carrierId}:`, {
                                        vehicles_assigned: assignment.vehicles_assigned,
                                        vehicles_accepted: assignment.vehicles_accepted,
                                        status: assignment.status,
                                        running_total_assigned: groups[carrierId].assignedVehicles
                                      });
                                     return groups;
                                   }, {} as Record<string, { 
                                     carrier: any, 
                                     assignments: LoadAssignment[], 
                                     assignedVehicles: number,
                                     canceledVehicles: number,
                                     activeVehicles: number,
                                     completedVehicles: number,
                                     acceptedVehicles: number
                                   }>);
                                   
                                   console.log(`Order ${order.id} - Created ${Object.keys(carrierGroups).length} carrier groups from ${allOrderAssignments.length} total assignments:`, carrierGroups);

                                     return Object.entries(carrierGroups)
                                       // Filter out carriers with zero assigned vehicles
                                       .filter(([carrierId, group]) => {
                                         const hasAssignedVehicles = group.assignedVehicles > 0 || group.activeVehicles > 0;
                                         console.log(`Filtering carrier ${carrierId} (${group.carrier?.name}):`, {
                                           assignedVehicles: group.assignedVehicles,
                                           activeVehicles: group.activeVehicles,
                                           show: hasAssignedVehicles
                                         });
                                         return hasAssignedVehicles;
                                       })
                                       .sort(([, groupA], [, groupB]) => {
                                         // Get the latest assignment date for each group
                                         const getLatestDate = (group: { assignments: LoadAssignment[] }) => {
                                           const newestAssignment = group.assignments.reduce((latest, current) => {
                                             const latestDate = new Date(latest.accepted_at || latest.assigned_at);
                                             const currentDate = new Date(current.accepted_at || current.assigned_at);
                                             return currentDate > latestDate ? current : latest;
                                           });
                                           return new Date(newestAssignment.accepted_at || newestAssignment.assigned_at);
                                         };
                                         
                                         const dateA = getLatestDate(groupA);
                                         const dateB = getLatestDate(groupB);
                                         
                                         console.log('Sorting carriers:', {
                                           carrierA: groupA.carrier?.name,
                                           dateA: dateA.toISOString(),
                                           carrierB: groupB.carrier?.name,
                                           dateB: dateB.toISOString(),
                                           result: dateB.getTime() - dateA.getTime()
                                         });
                                         
                                         // Sort by most recent first (descending)
                                         return dateB.getTime() - dateA.getTime();
                                       })
                                      .map(([carrierId, group]) => (
                                      <div 
                                        key={`${order.id}-${carrierId}-${loadAssignments.length}`}
                                        data-carrier-id={carrierId}
                                        className="flex items-center gap-4 cursor-pointer hover:bg-muted/70 p-4 rounded-lg transition-all duration-200 border border-muted bg-background min-w-0 animate-fade-in hover-scale"
                                        onClick={() => {
                                          // Pre-compute carrier routes data for instant modal opening
                                          const carrierRoutes = getCarrierRoutes(group.assignments[0].carrier_org_id || '', order.id);
                                          setPrecomputedCarrierRoutes(carrierRoutes);
                                          setSelectedCarrier(group.assignments[0]);
                                          setSelectedLoadOrderId(order.id);
                                          setIsCarrierDialogOpen(true);
                                        }}
                                      >
                                        <div className="flex items-center gap-2">
                                          <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                                            {group.assignedVehicles}
                                          </div>
                                        </div>
                                        <div className="min-w-0 flex-1">
                                          <p className="font-medium text-sm truncate">
                                            {group.carrier?.name || 'Carrier'}
                                          </p>
                                          <p className="text-xs text-muted-foreground">
                                             {(() => {
                                               const newestAssignment = group.assignments.reduce((latest, current) => 
                                                 new Date(current.accepted_at || current.assigned_at) > new Date(latest.accepted_at || latest.assigned_at) 
                                                   ? current : latest
                                               );
                                               const newestDate = newestAssignment.accepted_at || newestAssignment.assigned_at;
                                               
                                               if (!newestDate) return 'Recently assigned';
                                               
                                               const now = new Date();
                                               const assignmentDate = new Date(newestDate);
                                               const diffInMinutes = Math.floor((now.getTime() - assignmentDate.getTime()) / (1000 * 60));
                                               
                                               if (diffInMinutes < 1) {
                                                 return 'Now';
                                               } else if (diffInMinutes <= 30) {
                                                 return `${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'} ago`;
                                               } else {
                                                 return formatAppDate(newestDate, language);
                                               }
                                             })()}
                                          </p>
                                       </div>
                                      <ChevronRight className={`w-4 h-4 text-muted-foreground flex-shrink-0 ${isRTL ? 'rotate-180' : ''}`} />
                                    </div>
                                  ));
                                })()}
                              </div>
                              );
                            })()}
                          </div>
                     </CardContent>
                   </Card>
                );
                })}
            </div>
          )}
        </div>
      </div>
      
      {/* Route Details Modal */}
      <Dialog open={isRouteDialogOpen} onOpenChange={setIsRouteDialogOpen}>
        <DialogContent className="max-w-xl p-10">
          <DialogHeader>
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="w-6 h-6" />
              <div>
                <DialogTitle className="text-lg font-semibold">
                  {selectedRoute ? formatRoute(selectedRoute.pickup_city, selectedRoute.delivery_city) : 'Route Details'}
                </DialogTitle>
                {/* Route Reference */}
                {selectedRoute && (
                  <div className="text-xs text-muted-foreground mt-1">
                    <span>{selectedRoute.id.substring(0, 8).toUpperCase()}</span>
                  </div>
                )}
              </div>
            </div>
          </DialogHeader>
          
          {selectedRoute && (
            <div className="space-y-4">
              {/* Date and Truck Info */}
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span className="font-semibold text-foreground">
                    {selectedRoute.pickup_date ? formatAppDate(selectedRoute.pickup_date, language) : 'Tomorrow 6:00 AM'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Truck className="w-4 h-4 text-muted-foreground" />
                  <span className="font-semibold text-foreground">{selectedRoute.vehicles_needed || 1} {t('trucks.trucks')}</span>
                </div>
              </div>

              {/* Progress */}
              <div>
               <div className="flex items-center justify-between mb-2">
                 <span className="text-sm font-medium text-muted-foreground">{t('status.confirmed')}</span>
                 <span className="text-sm font-medium text-muted-foreground">
                   {selectedRoute.vehicles_assigned || 0}/{selectedRoute.vehicles_needed || 1}
                 </span>
               </div>
               <div className="w-full bg-muted rounded-full h-2">
                 <div 
                   className="h-2 rounded-full bg-green-500 transition-all duration-300"
                   style={{
                     width: `${(() => {
                       const assignedCount = selectedRoute.vehicles_assigned || 0;
                       const totalNeeded = selectedRoute.vehicles_needed || 1;
                       return Math.min(100, (assignedCount / totalNeeded) * 100);
                     })()}%` 
                   }}
                 ></div>
               </div>
              </div>

              {/* Accepted Carriers */}
              <div>
                <h4 className="text-sm font-medium text-foreground mb-3">{t('carriers.acceptedCarriers')}</h4>
                 {(() => {
                   const routeAssignments = getAssignmentsForRoute(selectedRoute.id);
                   const acceptedAssignments = routeAssignments.filter(a => (a.vehicles_assigned || 0) > 0);
                  
                  if (acceptedAssignments.length === 0) {
                    return (
                      <p className="text-sm text-muted-foreground">No carriers accepted yet</p>
                    );
                  }
                  
                  // Group assignments by carrier
                  const carrierGroups = acceptedAssignments.reduce((groups, assignment) => {
                    const carrierId = assignment.carrier_org_id || 'unknown';
                    if (!groups[carrierId]) {
                      groups[carrierId] = {
                        carrier: assignment.carrier,
                        assignments: [],
                        assignedVehicles: 0,
                        canceledVehicles: 0,
                        activeVehicles: 0,
                        completedVehicles: 0,
                        acceptedVehicles: 0
                      };
                    }
                    groups[carrierId].assignments.push(assignment);
                    groups[carrierId].assignedVehicles += (assignment.vehicles_assigned || 0);
                    groups[carrierId].canceledVehicles += (assignment.vehicles_canceled || 0);
                    groups[carrierId].activeVehicles += (assignment.vehicles_active || 0);
                    groups[carrierId].completedVehicles += (assignment.vehicles_completed || 0);
                    groups[carrierId].acceptedVehicles += (assignment.vehicles_accepted || 0);
                    return groups;
                  }, {} as Record<string, { 
                    carrier: any, 
                    assignments: LoadAssignment[], 
                    assignedVehicles: number,
                    canceledVehicles: number,
                    activeVehicles: number,
                    completedVehicles: number,
                    acceptedVehicles: number
                  }>);
                  
                  return (
                    <div className="space-y-3">
                      {Object.entries(carrierGroups).map(([carrierId, group]) => (
                        <div key={carrierId} className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                              {group.assignedVehicles}
                            </div>
                            <div>
                               <p className="font-medium text-sm">
                                 {group.carrier?.name || 'Carrier'}
                               </p>
                              <p className="text-sm text-muted-foreground">
                                {(() => {
                                  const newestAssignment = group.assignments.reduce((latest, current) => 
                                    new Date(current.accepted_at || current.assigned_at) > new Date(latest.accepted_at || latest.assigned_at) 
                                      ? current : latest
                                  );
                                  const newestDate = newestAssignment.accepted_at || newestAssignment.assigned_at;
                                  return newestDate ? formatAppDate(newestDate, language) : 'Recently accepted';
                                })()}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Truck className="w-4 h-4 text-primary" />
                            <span className="font-medium text-foreground">
                              {group.assignedVehicles} {t('trucks.trucksAssigned')}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                 );
               })()}
             </div>
           </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Carrier Routes Modal */}
      <Dialog open={isCarrierDialogOpen} onOpenChange={setIsCarrierDialogOpen}>
        <DialogContent className="max-w-2xl p-6">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <Truck className="w-5 h-5" />
              <DialogTitle>Carrier Routes</DialogTitle>
            </div>
          </DialogHeader>
          
          {selectedCarrier && (
            <div className="space-y-4">
              {/* Carrier Header */}
              <div className="flex items-center gap-3 p-4 bg-muted/30 rounded-lg">
                 <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center text-white text-lg font-bold">
                    {(() => {
                      return precomputedCarrierRoutes.reduce((total, route: any) => total + (route.assignedVehicles || 0), 0);
                    })()}
                 </div>
                 <div>
                    <h3 className="text-lg font-semibold text-foreground">
                      {selectedCarrier.carrier?.name || 'Carrier'}
                    </h3>
                   <p className="text-sm text-muted-foreground">
                      {(() => {
                        const totalTrucks = precomputedCarrierRoutes.reduce((total, route: any) => total + (route.assignedVehicles || 0), 0);
                        return `Total of ${totalTrucks} trucks for this load order`;
                      })()}
                   </p>
                 </div>
              </div>

              {/* Routes List */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-foreground">Accepted Routes</h4>
                {(() => {
                  if (precomputedCarrierRoutes.length === 0) {
                    return (
                      <p className="text-sm text-muted-foreground">No routes found for this carrier in this load order</p>
                    );
                  }
                  
                  return (
                    <div className="space-y-3">
                      {precomputedCarrierRoutes.map((route: any) => (
                        <div key={route.id} className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
                          <div className="flex items-center gap-3">
                            <MapPin className="w-4 h-4 text-muted-foreground" />
                            <div>
                              <p className="font-medium text-sm">
                                {formatRoute(route.pickup_city, route.delivery_city)}
                              </p>
                               <p className="text-xs text-muted-foreground">
                                 {route.id.substring(0, 8).toUpperCase()}
                               </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                             <div className="text-right">
                               <p className="text-sm font-medium text-foreground">
                                 {route.assignedVehicles} trucks
                               </p>
                               <p className="text-xs text-muted-foreground">
                                 {(() => {
                                   const latestAssignment = route.assignments?.reduce((latest: any, current: any) => 
                                     new Date(current.accepted_at || current.assigned_at) > new Date(latest.accepted_at || latest.assigned_at) 
                                       ? current : latest
                                   );
                                   const latestDate = latestAssignment?.accepted_at || latestAssignment?.assigned_at;
                                   return latestDate ? formatAppDate(latestDate, language) : 'Recently assigned';
                                 })()}
                               </p>
                            </div>
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Mobile Floating Action Button */}
      {user && (
        <div className="fixed bottom-6 right-6 md:hidden z-50">
          <Button 
            onClick={() => navigate('/new-load')} 
            className="h-16 w-16 rounded-full shadow-lg"
            variant="accent"
          >
            <Plus className="h-7 w-7" />
          </Button>
        </div>
      )}
      
      <InviteCarrierModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        onInviteSent={() => {
          setIsInviteModalOpen(false);
          // Optionally refresh data or show success message
        }}
      />
    </div>
  );
};